import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, Loader2, Home, Briefcase, Star, Building, Hash, ArrowUp } from 'lucide-react';
import { triggerNotification, triggerHaptic } from '../../utils/haptics';

const AddAddressPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

  const editData = location.state?.addressToEdit;

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    city: 'Саратов',
    street: '',
    house: '',
    corps: '', // [NEW] Поле для корпуса
    entrance: '',
    floor: '',
    flat: '',
    comment: '',
    label: 'home'
  });

  const [editId, setEditId] = useState(null);

  useEffect(() => {
    if (editData) {
      setEditId(editData.id);
      try {
        const parsed = JSON.parse(editData.address);
        setFormData({
            city: parsed.city || 'Саратов',
            street: parsed.street || '',
            house: parsed.house || '',
            corps: parsed.corps || '', // [NEW] Загружаем корпус если есть
            entrance: parsed.details?.entrance || '',
            floor: parsed.details?.floor || '',
            flat: parsed.details?.flat || '',
            comment: parsed.details?.comment || '',
            label: parsed.label || 'home'
        });
      } catch (e) {
        // Fallback для старых адресов, где была просто строка
        setFormData(prev => ({ ...prev, street: editData.address }));
      }
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.street || !formData.house) {
        alert('Укажите улицу и дом');
        return;
    }

    setIsLoading(true);
    if (typeof triggerHaptic === 'function') triggerHaptic('medium');

    // [FIX] Формируем красивую строку с учетом корпуса
    let fullString = `г. ${formData.city}, ул. ${formData.street}, д. ${formData.house}`;
    if (formData.corps) fullString += `, корп. ${formData.corps}`;
    if (formData.flat) fullString += `, кв. ${formData.flat}`;

    const addressJson = JSON.stringify({
        main_text: fullString,
        city: formData.city,
        street: formData.street,
        house: formData.house,
        corps: formData.corps, // [NEW] Сохраняем отдельно
        label: formData.label,
        details: {
            flat: formData.flat,
            floor: formData.floor,
            entrance: formData.entrance,
            comment: formData.comment
        }
    });

    try {
        const userId = tgUser?.id;
        if (!userId) {
            alert('Пожалуйста, откройте витрину через Telegram бота для сохранения адреса');
            setIsLoading(false);
            return;
        }

        if (editId) {
            await axios.post('/api/address/update', { user_id: userId, id: editId, address: addressJson });
        } else {
            await axios.post('/api/add-address', { user_id: userId, address: addressJson });
        }
        
        if (typeof triggerNotification === 'function') triggerNotification('success');
        navigate('/profile/addresses');
    } catch (e) {
        console.error(e);
        alert('Ошибка при сохранении');
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-tg-bg pb-40 text-tg-text">
      <div className="px-4 pb-4 pt-tg flex items-center sticky top-0 bg-tg-bg/95 backdrop-blur-md z-10 border-b border-gray-100/5">
        <button onClick={() => navigate('/profile/addresses')} className="mr-3 text-tg-text p-1 active:opacity-60">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-lg font-bold">{editId ? 'Редактировать' : 'Новый адрес'}</h1>
      </div>

      <div className="p-4 space-y-4">
        {/* Город */}
        <div className="bg-tg-secondary-bg p-3 rounded-xl border border-transparent focus-within:border-blue-500 transition-colors">
            <label className="text-[10px] uppercase font-bold text-tg-hint block mb-1">Город</label>
            <input name="city" value={formData.city} onChange={handleChange} className="w-full bg-transparent outline-none font-medium text-tg-text" />
        </div>

        {/* Улица (теперь на всю ширину) */}
        <div className="bg-tg-secondary-bg p-3 rounded-xl border border-transparent focus-within:border-blue-500 transition-colors">
            <label className="text-[10px] uppercase font-bold text-tg-hint block mb-1">Улица</label>
            <input name="street" value={formData.street} onChange={handleChange} className="w-full bg-transparent outline-none font-medium text-tg-text placeholder-gray-500" placeholder="Название улицы" />
        </div>

        {/* [NEW] Дом и Корпус в один ряд */}
        <div className="grid grid-cols-2 gap-3">
            <div className="bg-tg-secondary-bg p-3 rounded-xl border border-transparent focus-within:border-blue-500 transition-colors">
                <label className="text-[10px] uppercase font-bold text-tg-hint block mb-1">Дом</label>
                <input name="house" value={formData.house} onChange={handleChange} className="w-full bg-transparent outline-none font-medium text-tg-text" />
            </div>
            <div className="bg-tg-secondary-bg p-3 rounded-xl border border-transparent focus-within:border-blue-500 transition-colors">
                <label className="text-[10px] uppercase font-bold text-tg-hint block mb-1">Корпус</label>
                <input name="corps" value={formData.corps} onChange={handleChange} className="w-full bg-transparent outline-none font-medium text-tg-text" placeholder="" />
            </div>
        </div>

        {/* Квартира, Подъезд, Этаж */}
        <div className="grid grid-cols-3 gap-3">
            <div className="bg-tg-secondary-bg p-3 rounded-xl flex items-center gap-2 border border-transparent focus-within:border-blue-500">
                <Hash size={16} className="text-tg-hint shrink-0"/>
                <input name="flat" placeholder="Кв." value={formData.flat} onChange={handleChange} className="w-full bg-transparent outline-none text-sm" />
            </div>
            <div className="bg-tg-secondary-bg p-3 rounded-xl flex items-center gap-2 border border-transparent focus-within:border-blue-500">
                <Building size={16} className="text-tg-hint shrink-0"/>
                <input name="entrance" placeholder="Под." value={formData.entrance} onChange={handleChange} className="w-full bg-transparent outline-none text-sm" />
            </div>
            <div className="bg-tg-secondary-bg p-3 rounded-xl flex items-center gap-2 border border-transparent focus-within:border-blue-500">
                <ArrowUp size={16} className="text-tg-hint shrink-0"/>
                <input name="floor" placeholder="Эт." value={formData.floor} onChange={handleChange} className="w-full bg-transparent outline-none text-sm" />
            </div>
        </div>

        {/* Комментарий */}
        <div className="bg-tg-secondary-bg p-3 rounded-xl border border-transparent focus-within:border-blue-500">
            <label className="text-[10px] uppercase font-bold text-tg-hint block mb-1">Комментарий</label>
            <textarea name="comment" value={formData.comment} onChange={handleChange} className="w-full bg-transparent outline-none h-20 resize-none text-sm placeholder-gray-500" placeholder="Код домофона, этаж..." />
        </div>

        {/* Метки */}
        <div className="flex gap-3">
            <LabelBtn active={formData.label==='home'} onClick={()=>setFormData(p=>({...p, label:'home'}))} text="Дом" icon={Home} />
            <LabelBtn active={formData.label==='work'} onClick={()=>setFormData(p=>({...p, label:'work'}))} text="Работа" icon={Briefcase} />
            <LabelBtn active={formData.label==='other'} onClick={()=>setFormData(p=>({...p, label:'other'}))} text="Другое" icon={Star} />
        </div>
      </div>

      <div className="fixed bottom-[100px] left-4 right-4 z-30">
        <button onClick={handleSave} disabled={isLoading} className="w-full bg-tg-button text-white py-4 rounded-2xl font-bold flex justify-center items-center gap-2 shadow-lg active:scale-95 transition-transform">
            {isLoading ? <Loader2 className="animate-spin" /> : <Check />}
            <span>Сохранить</span>
        </button>
      </div>
    </div>
  );
};

const LabelBtn = ({ active, onClick, text, icon: Icon }) => (
    <button onClick={onClick} className={`flex-1 py-3 rounded-xl text-[10px] font-bold transition-all border-2 flex flex-col items-center gap-1 ${active ? 'bg-tg-button text-white border-tg-button' : 'bg-tg-secondary-bg text-tg-hint border-transparent'}`}>
        <Icon size={18} />{text}
    </button>
);

export default AddAddressPage;
